import { ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { contactUs, getDictationWebUrl } from "@/product-links";
import { useAppStore } from "@/controller";
import { useUISlice } from "@/models/useUISlice";
import { logClientEvent } from "@/telemetry";
import {
  CheckIcon,
  CloseIcon,
  CrownIcon,
  FileTextIcon,
  HelpCircleIcon,
  LightbulbIcon,
  MicrophoneFilledIcon,
  TranslateIcon,
} from "@/components/Icons";
import { getPlatform } from "@/platform";
import { i18n } from "@/locales";
import { useRouter, type Path } from "@/_router";
import { actions } from "@/controller";
import { selectLatestUsableTranscriptId } from "@/store";
import toast from "react-hot-toast";

const GOOGLE_PLAY_CANCEL_LANGS = [
  "af", "az", "da", "de", "et", "en-GB", "es", "es-419", "fil", "fr",
  "fr-CA", "ga", "hr", "id", "it", "lv", "lt", "hu", "mt", "ms",
  "nl", "no", "pl", "pt", "pt-BR", "ro", "sk", "sl", "fi", "sv",
  "vi", "tr", "is", "cs", "el", "bg", "ru", "sr", "uk", "iw",
  "ur", "ar", "fa", "mr", "hi", "bn", "gu", "ta", "te", "kn",
  "th", "zh-Hans", "zh-Hant", "zh-HK", "ja", "ko", "en",
] as const;

const getGooglePlayCancelUrl = () => {
  const lang = i18n.language;
  const hl = GOOGLE_PLAY_CANCEL_LANGS.find(
    (l) => lang === l || lang.startsWith(l.split("-")[0])
  ) || "en";
  return `https://support.google.com/googleplay/answer/7018481?hl=${hl}&co=GENIE.Platform%3DAndroid`;
};

const CANCELLATION_REASONS = [
  { key: "too_expensive", i18nKey: "settings.cancelReasonExpensive" },
  { key: "not_using_enough", i18nKey: "settings.cancelReasonNotUsing" },
  { key: "quality_not_good", i18nKey: "settings.cancelReasonQuality" },
  { key: "found_alternative", i18nKey: "settings.cancelReasonAlternative" },
  { key: "missing_features", i18nKey: "settings.cancelReasonMissing" },
  { key: "other", i18nKey: "settings.cancelReasonOther" },
] as const;

// Reasons that trigger the feature-reminder intercept.
const INTERCEPT_REASONS = new Set<string>([
  "not_using_enough",
  "too_expensive",
]);

type UnusedFeatureKey =
  | "translate"
  | "summary"
  | "quiz"
  | "flashcard"
  | "dictation";

type UnusedFeature = {
  key: UnusedFeatureKey;
  // Null for dictation, which opens its capability-specific surface.
  deepLinkPath: Path | null;
};

// One canonical product route per feature.
const FEATURE_CATALOG: ReadonlyArray<{
  key: UnusedFeatureKey;
  routePath: Path | null;
}> = [
  { key: "translate", routePath: "/translate" },
  { key: "summary", routePath: "/summary" },
  { key: "quiz", routePath: "/quiz" },
  { key: "flashcard", routePath: "/flashcard" },
  { key: "dictation", routePath: null },
];

// Public URL the desktop / iOS users land on for dictation is built per
// request via `getDictationWebUrl()` so the user lands on the correct
// localised page. The user's existing subscription covers this surface —
// that's the whole point of surfacing it inside the save-attempt.

// Max cards shown on the intercept screen.
const MAX_INTERCEPT_CARDS = 4;

const FEATURE_ICONS: Record<
  UnusedFeatureKey,
  (props: { size?: number; className?: string }) => ReactElement
> = {
  translate: TranslateIcon,
  summary: FileTextIcon,
  quiz: HelpCircleIcon,
  flashcard: LightbulbIcon,
  dictation: MicrophoneFilledIcon,
};

export default function CancelSubscriptionForm({
  onClose,
}: {
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [freeText, setFreeText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showIntercept, setShowIntercept] = useState(false);
  // Tracks whether the intercept has been shown this session so a second
  // Submit tap proceeds straight to the email-support flow.
  const [interceptShownOnce, setInterceptShownOnce] = useState(false);

  // Derive feature-usage state once on mount. We pull `featureFirstUsedAt`
  // directly off the store rather than via a selector so this component does
  // not subscribe to every userSlice change. The field is optional — until
  // it ships we treat every feature as unused (safe default; intercept is
  // opt-in by reason anyway).
  const { dictationUnused, otherUnusedFeatures, allUnusedFeatures } =
    useMemo(() => {
      const state = useAppStore.getState() as Record<string, unknown>;
      const featureFirstUsedAt =
        (state.featureFirstUsedAt as
          | Partial<Record<UnusedFeatureKey, number>>
          | undefined) || {};
      const all: UnusedFeature[] = FEATURE_CATALOG.filter(
        (f) => !featureFirstUsedAt[f.key]
      ).map((f) => ({
        key: f.key,
        deepLinkPath: f.routePath,
      }));
      const dictation = !featureFirstUsedAt.dictation;
      const others = all
        .filter((f) => f.key !== "dictation")
        .slice(0, MAX_INTERCEPT_CARDS);
      return {
        dictationUnused: dictation,
        otherUnusedFeatures: others,
        allUnusedFeatures: all,
      };
    }, []);

  const toggleReason = (key: string) => {
    setSelectedReasons((prev) =>
      prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]
    );
  };

  const submitCancellationEmail = async () => {
    logClientEvent("subscription.cancelRequested", {
      reasons: selectedReasons,
    });

    // Web can represent a Stripe, App Store, or Google Play subscription.
    // The server-owned provider selects the real management destination;
    // unknown ownership never falls back to Stripe.
    if (getPlatform() === "web") {
      const result = await actions.subscription.manage();
      if ("error" in result) {
        toast.error(t("useAuth.signInFailed"));
        return;
      }
      window.open(result.value, "_self");
      return;
    }

    const reasonLabels = selectedReasons
      .map((key) => {
        const reason = CANCELLATION_REASONS.find((r) => r.key === key);
        return reason ? t(reason.i18nKey) : key;
      })
      .join(", ");

    const body = [
      t("settings.cancelEmailBody"),
      ``,
      t("settings.cancelEmailReasons", { reasons: reasonLabels }),
      freeText
        ? `\n${t("settings.cancelEmailFeedback", { feedback: freeText })}`
        : undefined,
      getPlatform() === "android"
        ? `\n${t("settings.cancelEmailGooglePlay")}\n${getGooglePlayCancelUrl()}`
        : undefined,
    ]
      .filter((line) => line !== undefined)
      .join("\n");

    contactUs(
      encodeURIComponent(
        `${t("settings.cancelEmailSubject")} (#DO NOT REMOVE: ${useAppStore.getState()?.deviceId})`
      ),
      encodeURIComponent(body)
    );

    setSubmitted(true);
  };

  const handleSubmit = () => {
    if (selectedReasons.length === 0) return;

    // Feature-reminder intercept: only on first submit, only for the two
    // reasons that map to "you're not getting your money's worth", and only
    // if we actually have unused features to recommend.
    const reasonQualifies = selectedReasons.some((r) =>
      INTERCEPT_REASONS.has(r)
    );
    if (
      !interceptShownOnce &&
      reasonQualifies &&
      allUnusedFeatures.length > 0
    ) {
      logClientEvent("subscription.cancelOfferShown", {
        reasons: selectedReasons,
        unusedFeatures: allUnusedFeatures.map((f) => f.key),
        dictationUnused,
      });
      setShowIntercept(true);
      return;
    }

    submitCancellationEmail();
  };

  const handleTryNow = (feature: UnusedFeature) => {
    logClientEvent(
      "subscription.cancelFeatureSelected",
      { featureKey: feature.key }
    );
    // Dismiss the cancel form before navigating so the modal doesn't
    // overlay the destination screen.
    onClose();

    if (feature.key === "dictation") {
      handleTryDictation();
      return;
    }

    if (feature.deepLinkPath) {
      // The destination pages (translate/summary/quiz/flashcard) all depend
      // on the typed transcript query id. Without an id they dead-end with
      // empty state. Look up the latest completed transcript and append it.
      const transcripts = (useAppStore.getState() as any)?.transcripts;
      const id = selectLatestUsableTranscriptId(transcripts);
      if (id) {
        router.push({
          pathname: feature.deepLinkPath,
          search: `?${new URLSearchParams({ id })}`,
        });
      } else {
        // No usable transcript — send the user home to create one rather
        // than landing on a blank feature page.
        router.push("/home");
      }
    }
  };

  // Dedicated handler for the dictation hero — branches by platform. Android
  // (floating bubble) and iOS (custom keyboard) have in-app dictation — open
  // the onboarding modal; web opens the desktop dictation site, where the
  // same subscription unlocks Mac/Windows dictation. Using web users as a
  // lead-gen channel for the desktop product is intentional.
  const handleTryDictation = () => {
    const platform = getPlatform();
    const hasInAppDictation = platform === "android" || platform === "ios";
    logClientEvent(
      "subscription.cancelFeatureSelected",
      {
        featureKey: "dictation",
        destination: hasInAppDictation ? "in-app" : "web",
      }
    );
    onClose();
    if (hasInAppDictation) {
      // Same pattern the notification tap uses: navigate to home first so
      // its listener is mounted, then dispatch the open event one tick later.
      router.push("/home");
      setTimeout(() => {
        try {
          window.dispatchEvent(
            new CustomEvent("openGlobalDictationOnboarding")
          );
        } catch {
          // CustomEvent unavailable in some non-DOM contexts (SSR/tests).
        }
      }, 200);
      return;
    }
    // Web — open the desktop dictation product. The existing subscription
    // covers it.
    try {
      window.open(getDictationWebUrl(), "_blank", "noopener,noreferrer");
    } catch {
      router.push("/home");
    }
  };

  const handleContinueCancelling = () => {
    logClientEvent(
      "subscription.cancelConfirmed"
    );
    setInterceptShownOnce(true);
    // Return to the reason-picker so the user can re-confirm before we fire
    // the email. They've already chosen reasons; another tap on Submit goes
    // straight through (interceptShownOnce gates the intercept).
    setShowIntercept(false);
  };

  if (submitted) {
    return (
      <div className="scrim fixed inset-0 z-50 flex items-center justify-center-sm">
        <div className="glass-thick mx-4 w-full max-w-[400px] rounded-3xl p-6">
          <div className="flex justify-end">
            <button
              data-action-id="subscription.cancel.close"
              onClick={onClose}
              className="icon-btn icon-btn-quiet"
            >
              <CloseIcon size={20} />
            </button>
          </div>
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-[var(--accent)] mx-auto mb-3 flex items-center justify-center">
              <CheckIcon size={24} className="text-[var(--text-inverse)]" />
            </div>
            <p className="text-[15px] text-[var(--text-secondary)]">
              {t("settings.cancelConfirmation")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showIntercept) {
    return (
      <div className="scrim fixed inset-0 z-50 flex items-center justify-center-sm">
        <div className="glass-thick mx-4 w-full max-w-[420px] rounded-3xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between mb-3 gap-3">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)] leading-snug">
              {t("settings.cancelIntercept.title")}
            </h2>
            <button
              data-action-id="subscription.cancel.close"
              onClick={onClose}
              className="icon-btn icon-btn-quiet shrink-0"
            >
              <CloseIcon size={20} />
            </button>
          </div>

          <p className="text-[14px] text-[var(--text-secondary)] mb-5">
            {t("settings.cancelIntercept.subtitle")}
          </p>

          {/* Annual-savings reframe — shown only when "too expensive" is one
              of the selected reasons. Routes back into the paywall so the
              user can actually downgrade to yearly without leaving the app.
              Theme tokens: this codebase flips `--accent` (white in dark
              mode, black in light), so we pair it with `--text-inverse`
              which is always the readable contrast on top of accent. */}
          {selectedReasons.includes("too_expensive") && (
            <button
              data-action-id="subscription.cancel.offer.annual"
              onClick={() => {
                logClientEvent(
                  "subscription.cancelFeatureSelected",
                  { featureKey: "annual-savings-reframe" }
                );
                onClose();
                useUISlice.getState().setPaywallVisible({
                  utmSource: "cancel-intercept --> too-expensive",
                });
              }}
              className="w-full rounded-2xl bg-gradient-to-br from-[var(--accent)] to-[var(--accent)]/80 p-4 flex items-start gap-3 mb-3 text-start active:scale-[0.99] transition-transform shadow-md"
            >
              <div className="w-10 h-10 rounded-full bg-[var(--text-inverse)]/15 flex items-center justify-center shrink-0">
                <CrownIcon
                  size={20}
                  className="text-[var(--text-inverse)]"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[15px] font-semibold text-[var(--text-inverse)] mb-1">
                  {t("settings.cancelIntercept.annualReframe.title")}
                </h3>
                <p className="text-[13px] text-[var(--text-inverse)]/90 leading-relaxed">
                  {t("settings.cancelIntercept.annualReframe.body")}
                </p>
              </div>
            </button>
          )}

          {/* Hero: Dictation save-attempt. Sells the feature hard when the
              user hasn't tried it yet — sets up phone use (Android-only
              in-app) AND the desktop dictation site (Mac/Windows), since the
              same subscription covers both surfaces. The desktop pitch is
              the strategic lead-gen for the 1transcribe.com/dictation
              product, especially on iOS where in-app dictation isn't
              available yet. */}
          {dictationUnused && (
            <button
              data-action-id="subscription.cancel.offer.dictation"
              onClick={handleTryDictation}
              className="w-full rounded-2xl bg-gradient-to-br from-[var(--accent)] to-[var(--accent)]/85 p-4 flex flex-col gap-3 mb-4 text-start active:scale-[0.99] transition-transform shadow-md"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-[var(--text-inverse)]/15 flex items-center justify-center shrink-0">
                  <MicrophoneFilledIcon
                    size={22}
                    className="text-[var(--text-inverse)]"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[16px] font-bold text-[var(--text-inverse)] mb-1 leading-snug">
                    {t("settings.cancelIntercept.dictationHero.title")}
                  </h3>
                  <p className="text-[13px] text-[var(--text-inverse)]/90 leading-relaxed">
                    {t("settings.cancelIntercept.dictationHero.body")}
                  </p>
                </div>
              </div>
              <ul className="flex flex-col gap-1.5 text-[12.5px] leading-snug text-[var(--text-inverse)]/95">
                <li>
                  {t("settings.cancelIntercept.dictationHero.bulletPhone")}
                </li>
                <li>
                  {t("settings.cancelIntercept.dictationHero.bulletComputer")}
                </li>
                <li>
                  {t("settings.cancelIntercept.dictationHero.bulletIncluded")}
                </li>
              </ul>
              <span className="self-start mt-1 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[var(--text-inverse)] text-[var(--accent)] text-[13px] font-semibold">
                {getPlatform() === "android"
                  ? t("settings.cancelIntercept.dictationHero.ctaAndroid")
                  : t("settings.cancelIntercept.dictationHero.ctaOther")}
              </span>
            </button>
          )}

          {/* Secondary features — smaller listing below. When dictation is
              the lead card, this becomes a "plus these are also included"
              section; when dictation is already in use it's the only group
              and uses no heading. */}
          {otherUnusedFeatures.length > 0 && (
            <div className="mb-5">
              {dictationUnused && (
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-2 px-1">
                  {t(
                    "settings.cancelIntercept.dictationHero.otherFeaturesHeading"
                  )}
                </p>
              )}
              <div className="space-y-2">
                {otherUnusedFeatures.map((feature) => {
                  const Icon = FEATURE_ICONS[feature.key];
                  return (
                    <button
                      key={feature.key}
                      data-action-id="subscription.cancel.offer.feature"
                      onClick={() => handleTryNow(feature)}
                      className="w-full rounded-2xl glass-tile p-3 flex items-center gap-3 text-start active:bg-[var(--glass-bg-active)] transition-colors"
                    >
                      <div className="w-9 h-9 rounded-full bg-[var(--fill-2)] flex items-center justify-center shrink-0">
                        <Icon
                          size={16}
                          className="text-[var(--text-primary)]"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-semibold text-[var(--text-primary)] leading-tight">
                          {t(
                            `settings.cancelIntercept.${feature.key}.title`
                          )}
                        </p>
                        <p className="text-[12px] text-[var(--text-secondary)] leading-snug mt-0.5 line-clamp-2">
                          {t(
                            `settings.cancelIntercept.${feature.key}.body`
                          )}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button
            data-action-id="subscription.cancel.continue"
            onClick={handleContinueCancelling}
            className="w-full text-center text-[14px] text-[var(--text-muted)] underline underline-offset-2 hover:text-[var(--text-secondary)] transition-all py-2"
          >
            {t("settings.cancelIntercept.continueCancelling")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="scrim fixed inset-0 z-50 flex items-center justify-center-sm">
      <div className="glass-thick mx-4 w-full max-w-[400px] rounded-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
            {t("settings.cancelSubscription")}
          </h2>
          <button
            data-action-id="subscription.cancel.close"
            onClick={onClose}
            className="icon-btn icon-btn-quiet"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <p className="text-[14px] text-[var(--text-secondary)] mb-5">
          {t("settings.cancelSubscriptionSubtitle")}
        </p>

        <div className="space-y-2 mb-5">
          {CANCELLATION_REASONS.map(({ key, i18nKey }) => (
            <button
              key={key}
              data-action-id="subscription.cancel.reason.toggle"
              onClick={() => toggleReason(key)}
              className={`w-full px-4 py-3 rounded-xl text-start text-[14px] transition-all flex items-center gap-3 ${
                selectedReasons.includes(key)
                  ? "bg-[var(--accent)] text-[var(--text-inverse)]"
                  : "glass-tile text-[var(--text-primary)] hover:bg-[var(--glass-bg-hover)]"
              }`}
            >
              <div
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                  selectedReasons.includes(key)
                    ? "border-[var(--text-inverse)] bg-transparent"
                    : "border-[var(--text-muted)]"
                }`}
              >
                {selectedReasons.includes(key) && (
                  <CheckIcon
                    size={14}
                    className="text-[var(--text-inverse)]"
                  />
                )}
              </div>
              {t(i18nKey)}
            </button>
          ))}
        </div>

        <textarea
          data-action-id="subscription.cancel.feedback.change"
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder={t("settings.cancelFeedbackPlaceholder")}
          className="w-full h-24 px-4 py-3 rounded-xl glass-tile text-[14px] text-[var(--text-primary)] placeholder-[var(--text-muted)] resize-none outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all mb-5"
        />

        <button
          data-action-id="subscription.cancel.submit"
          onClick={handleSubmit}
          disabled={selectedReasons.length === 0}
          className="btn btn-danger w-full text-[15px] flex items-center justify-center active:scale-[0.97] transition-all disabled:opacity-40"
        >
          {t("settings.cancelSubmit")}
        </button>
      </div>
    </div>
  );
}
